import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import {
  FileText, Award, CheckCircle2, MapPin, BellRing, MessageSquareReply, UserCheck,
  ShieldCheck, Clock, Wrench, ArrowRight, Sparkles, Ban, Euro,
} from "lucide-react";

const STEPS = [
  { icon: BellRing, title: "Vous recevez la demande", text: "Dès qu'un particulier de votre secteur dépose une demande correspondant à votre métier, vous êtes prévenu par e-mail." },
  { icon: UserCheck, title: "Vous acceptez (ou non)", text: "Vous consultez la demande gratuitement et décidez de l'accepter. Les coordonnées du client vous sont alors communiquées." },
  { icon: MessageSquareReply, title: "Vous envoyez votre proposition", text: "Vous adressez votre devis ou votre estimation directement au particulier, sans intermédiaire." },
  { icon: CheckCircle2, title: "Vous décrochez le chantier", text: "Le particulier compare, vous contacte et vous réalisez la prestation. Vous développez votre clientèle locale." },
];

const PERKS = [
  { icon: Ban, title: "Aucun paiement par prospect", text: "Vous ne payez jamais pour recevoir une demande." },
  { icon: Euro, title: "Aucune commission", text: "Aucun pourcentage prélevé sur vos ventes ou vos chantiers." },
  { icon: MapPin, title: "Demandes locales et ciblées", text: "Uniquement des demandes de votre métier, dans vos communes et votre rayon d'intervention." },
  { icon: Clock, title: "Vous gardez le contrôle", text: "Métiers, zones, quotas par jour/mois et mise en pause : vous réglez tout depuis votre espace." },
  { icon: ShieldCheck, title: "Distribution maîtrisée", text: "Chaque demande n'est envoyée qu'à 3 professionnels maximum : moins de concurrence, plus de chances." },
  { icon: Sparkles, title: "100% intégré à votre compte pro", text: "Au même endroit que votre mini-site, vos offres et votre CRM PROXIreduc-28." },
];

export default function ProReceiveQuotes() {
  useSeo({
    title: "Recevez des demandes de devis gratuitement — Pros de l'Eure-et-Loir | PROXIreduc-28",
    description: "Artisans et professionnels de Chartres et de l'Eure-et-Loir : recevez gratuitement des demandes de devis de particuliers de votre secteur. Offre Pro Fondateur : 12 mois gratuits minimum, sans commission ni crédit.",
    canonicalPath: "/recevoir-des-devis",
  });

  return (
    <div data-testid="pro-receive-quotes-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#172A3A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#E05638] text-white text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 mb-5" data-testid="prq-founder-badge">
            <Award size={14} /> Pro Fondateur · 12 mois gratuits minimum
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold max-w-3xl leading-tight">
            Recevez des demandes de devis <span className="text-[#E05638]">près de chez vous</span>, gratuitement.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/80 max-w-2xl">
            Artisans, commerçants et professionnels de Chartres et de l'Eure-et-Loir : des particuliers cherchent vos services chaque jour. Recevez leurs demandes correspondant à votre métier et développez votre clientèle locale — <strong className="text-white">sans payer au prospect, sans commission, sans engagement.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/devenir-partenaire" data-testid="prq-cta-register">
              <Button className="h-12 px-7 text-base btn-shine text-white font-extrabold"><Award size={18} className="mr-2" /> Devenir Pro Fondateur — 0 €</Button>
            </Link>
            <Link to="/pour-les-professionnels" data-testid="prq-cta-learn">
              <Button variant="outline" className="h-12 px-6 text-base bg-white/5 border-white/25 text-white hover:bg-white/15">Voir tous les avantages</Button>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
            {["0 € pendant 12 mois minimum", "Aucun crédit à acheter", "Aucune commission"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-400" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="prq-how">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#172A3A] text-center">Comment ça marche ?</h2>
        <p className="mt-2 text-slate-600 text-center max-w-2xl mx-auto">De la demande du particulier au chantier décroché, en 4 étapes simples.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative bg-white border border-slate-200 rounded-2xl p-6 card-hover" data-testid="prq-step">
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#172A3A] text-white grid place-items-center font-extrabold text-sm">{i + 1}</div>
              <s.icon size={26} className="text-[#E05638] mb-3" />
              <div className="font-heading font-bold text-[#172A3A]">{s.title}</div>
              <p className="mt-1.5 text-sm text-slate-600">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OFFRE FONDATEUR */}
      <section className="bg-[#F2F4F5] border-y border-slate-100" data-testid="prq-founder">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="rounded-3xl bg-gradient-to-br from-[#172A3A] to-[#2a4258] text-white p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <Award size={30} className="text-[#E05638]" />
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Offre Pro Fondateur</h2>
            </div>
            <p className="text-white/85 max-w-2xl">
              Rejoignez le réseau des professionnels fondateurs de PROXIreduc-28. Pendant <strong className="text-white">au moins 12 mois</strong>, recevez gratuitement des demandes de devis correspondant à votre activité et à votre secteur géographique. Aucun paiement par prospect, aucun crédit à acheter, aucun engagement payant. Profitez de cette période de lancement pour développer votre clientèle locale — et bénéficiez d'un avantage de fidélité par rapport aux futurs inscrits.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/devenir-partenaire" data-testid="prq-founder-cta">
                <Button className="h-12 px-7 text-base btn-shine text-white font-extrabold">Activer ma place fondateur — 0 €</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="prq-perks">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#172A3A] text-center">Pourquoi c'est différent</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PERKS.map((p) => (
            <div key={p.title} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3 card-hover" data-testid="prq-perk">
              <div className="w-10 h-10 rounded-xl bg-orange-50 grid place-items-center shrink-0"><p.icon size={20} className="text-[#E05638]" /></div>
              <div>
                <div className="font-heading font-bold text-[#172A3A]">{p.title}</div>
                <p className="mt-1 text-sm text-slate-600">{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-[#172A3A] text-white" data-testid="prq-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <Wrench size={32} className="text-[#E05638] mx-auto mb-4" />
          <h2 className="font-heading text-2xl sm:text-4xl font-extrabold">Prêt à recevoir vos premières demandes ?</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">Rejoignez les Pros Fondateurs de PROXIreduc-28 et développez votre activité localement, gratuitement.</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/devenir-partenaire" data-testid="prq-final-register">
              <Button className="h-12 px-8 text-base btn-shine text-white font-extrabold">Devenir Pro Fondateur — 0 €</Button>
            </Link>
          </div>
          <p className="mt-5 text-xs text-white/50">Vous êtes un particulier ? <Link to="/demande-de-devis" className="underline hover:text-white" data-testid="prq-particulier-link">Demandez vos devis gratuits ici</Link>.</p>
        </div>
      </section>
    </div>
  );
}
